import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Modal, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import ReturnRegister from "../../pages/public/register/returnRegister";
import AddressStep from "../../pages/public/register/steps/address";
import AuthInfoStep from "../../pages/public/register/steps/authInfo";
import ContactStep from "../../pages/public/register/steps/contact";
import CpfCnpjStep from "../../pages/public/register/steps/cpfCnpj";
import RegisterDataStep from "../../pages/public/register/steps/registerData";
import { Steps } from "../../pages/public/register/stepUtils";
import { registerSchemaInput, register as registerApi } from "../../api/register";
import type { RegisterInput } from "../../api/register";
import "../../pages/public/register/style.css";

export interface ReturnRegisterLoaderData {
  state: "success" | "error";
}

export type States = ReturnRegisterLoaderData["state"];

function Register(): React.JSX.Element {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [emailProvided, setEmailProvided] = useState("");
  const [openModalExit, setOpenModalExit] = useState(false);
  const navigate = useNavigate();
  const [stateData, setStateData] = useState<States | undefined>(undefined);

  const methods = useForm<RegisterInput>({
    defaultValues: {
      cpf: "",
      name: "",
      password: "",
      confirmation_password: "",
      cellphone: "",
      phone: "",
      email: "",
      confirm_email: "",
      cep: "",
      state: "",
      city: "",
      street: "",
      streetNumber: "",
      neighborhood: "",
      complement: "",
    },
    resolver: yupResolver(registerSchemaInput),
    criteriaMode: "all",
    mode: "onChange",
  });

  const { handleSubmit } = methods;
  const emailValue = methods.watch("email");
  useEffect(() => {
    if (emailValue && methods.getValues("confirm_email")) {
      methods.trigger("confirm_email");
    }
  }, [emailValue, methods.getValues("confirm_email")]);

  const mutation = useMutation({
    mutationFn: async (input: RegisterInput) => registerApi(input),
    onSuccess: () => setStateData("success"),
    onError: () => setStateData("error"),
  });

  const onSubmit: SubmitHandler<RegisterInput> = (data: RegisterInput): void => {
    setEmailProvided(data.email);
    mutation.mutate(data);
  };

  const handleNext = (): void => {
    setCurrentStep((prevState) => prevState + 1);
  };

  const handleBack = (): void => {
    setCurrentStep((prevState) => prevState - 1);
  };

  const handleOpenModalExit = (): void => {
    setOpenModalExit(true);
  };
  const handleCloseModalExit = (): void => {
    setOpenModalExit(false);
  };

  const handleCancel = (): void => {
    handleOpenModalExit();
  };

  return (
    <Box component="section">
      {!stateData && (
        <Stack>
          <Box alignItems="center" display="flex" justifyContent="space-between">
            <Typography fontWeight="600" variant="h4">
              {t("register.createAccount")}
            </Typography>
            <p className="step-guide">
              {currentStep + 1}/{Steps().length}
            </p>
          </Box>
          <Typography color="text.secondary" fontWeight="400" variant="body2">
            {Steps()[currentStep].title}
          </Typography>
        </Stack>
      )}

      <Stack sx={{ marginTop: "5%" }}>
        <FormProvider {...methods}>
          <form autoComplete="off" autoSave="off" onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
            {Steps()[currentStep].id === 1 && !stateData && (
              <CpfCnpjStep handleCancel={handleCancel} handleNext={handleNext} />
            )}

            {Steps()[currentStep].id === 2 && !stateData && (
              <RegisterDataStep handleBack={handleBack} handleCancel={handleCancel} handleNext={handleNext} />
            )}

            {Steps()[currentStep].id === 3 && !stateData && (
              <ContactStep handleBack={handleBack} handleCancel={handleCancel} handleNext={handleNext} />
            )}

            {Steps()[currentStep].id === 4 && !stateData && (
              <AddressStep handleBack={handleBack} handleCancel={handleCancel} handleNext={handleNext} />
            )}

            {Steps()[currentStep].id === 5 && !stateData && (
              <AuthInfoStep handleBack={handleBack} handleCancel={handleCancel} />
            )}

            {stateData && emailProvided ? (
              <ReturnRegister emailProvided={emailProvided} handleBack={handleBack} stateData={stateData} />
            ) : null}
          </form>
        </FormProvider>
      </Stack>
      <Modal aria-describedby="spring-modal-description" aria-labelledby="spring-modal-title" onClose={handleCloseModalExit} open={openModalExit}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: "450px",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            overflow: "hidden",
            borderRadius: "20px",
          }}
        >
          <Typography component="h2" id="spring-modal-title" variant="h6">
            {t("register.abandonTitle")}
          </Typography>
          <Typography id="spring-modal-description" sx={{ mt: 2 }}>
            {t("register.abandonMessage")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              gap: "1rem",
              marginTop: "2rem",
            }}
          >
            <Button color="primary" onClick={handleCloseModalExit} size="large" tabIndex={0} type="button" variant="contained">
              {t("register.continueRegistering")}
            </Button>
            <Button
              color="success"
              onClick={() => {
                navigate({ to: "/login" });
              }}
              size="large"
              tabIndex={0}
              type="button"
              variant="outlined"
            >
              {t("register.exitRegistration")}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

export const Route = createFileRoute("/__public/register")({
  component: Register,
});
